import { useState, type CSSProperties, type ReactNode } from 'react';
import { api } from './lib/api';
import { IconBack } from './components/icons';

// Conversational authoring Phase 0 — the guided interview OPENER (vision doc §5 Phase 0). One calm question at a
// time: name → city → phone (optional) → specialty → stream → confirm. Answered steps collapse into editable summary
// rows so the "conversation so far" stays visible. On confirm: PATCH /api/profile (the existing Phase-4 contact
// header — partial, COALESCE) then hand specialty+stream up; the parent routes to authoring.
//
// FIREWALL: every field here is a FACT the user types/picks (name/city/phone/specialty/stream). The interview asks
// and structures; it never writes CV content. This is a conversation RAIL (directed interview), not an open chat —
// the §1 model the owner ratified. The standard form stays reachable via onSkip (additive, nothing dropped).

interface Specialty { id: string; name: string }
export interface Contact { name: string; phone: string; city: string }

type StepId = 'name' | 'city' | 'phone' | 'specialty' | 'stream' | 'confirm';
const ORDER: StepId[] = ['name', 'city', 'phone', 'specialty', 'stream', 'confirm'];

export function GuidedStart({ specialties, seed, onComplete, onBack, onSkip }: {
  specialties: Specialty[];
  seed: Contact;                       // existing profile values — prefilled so a returning user confirms, not re-types
  onComplete: (contact: Contact, specialtyId: string, stream: string) => void;
  onBack: () => void;                  // from the first step — back to the Craft door
  onSkip: () => void;                  // "Prefer a form?" — the classic start-fresh authoring surface
}) {
  const [step, setStep] = useState<StepId>('name');
  const [name, setName] = useState(seed.name);
  const [city, setCity] = useState(seed.city);
  const [phone, setPhone] = useState(seed.phone);
  const [specialtyId, setSpecialtyId] = useState('');
  const [stream, setStream] = useState(''); // 'img' | 'cmg' — EvalSetup's framing, reused
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const idx = ORDER.indexOf(step);
  const specName = specialties.find((s) => s.id === specialtyId)?.name ?? '';

  function advance() { setStep(ORDER[Math.min(idx + 1, ORDER.length - 1)]); }
  function retreat() { idx === 0 ? onBack() : setStep(ORDER[idx - 1]); }

  // Confirm — persist the contact header via the existing profile endpoint, then hand off to authoring.
  async function finish() {
    setSaving(true); setError('');
    const r = await api('/api/profile', {
      method: 'PATCH',
      body: JSON.stringify({ name: name.trim(), city: city.trim(), ...(phone.trim() ? { phone: phone.trim() } : {}) }),
    });
    setSaving(false);
    if (!r.ok) { setError("We couldn't save your details — check your connection and try again."); return; }
    onComplete({ name: name.trim(), phone: phone.trim(), city: city.trim() }, specialtyId, stream);
  }

  // A completed step, shown as a quiet summary row with an Edit jump-back (the visible "conversation so far").
  const done = (id: StepId, label: string, value: string) => (
    <div key={id} style={S.doneRow} data-testid={`guided-done-${id}`}>
      <span style={S.doneLabel}>{label}</span>
      <span style={S.doneValue}>{value}</span>
      <button className="btn btn-ghost" style={S.doneEdit} onClick={() => setStep(id)} data-testid={`guided-edit-${id}`}>Edit</button>
    </div>
  );
  const doneRows: ReactNode[] = [];
  if (idx > ORDER.indexOf('name') && name.trim()) doneRows.push(done('name', 'Name', name.trim()));
  if (idx > ORDER.indexOf('city') && city.trim()) doneRows.push(done('city', 'City', city.trim()));
  if (idx > ORDER.indexOf('phone') && phone.trim()) doneRows.push(done('phone', 'Phone', phone.trim()));
  if (idx > ORDER.indexOf('specialty') && specName) doneRows.push(done('specialty', 'Specialty', specName));
  if (idx > ORDER.indexOf('stream') && stream) doneRows.push(done('stream', 'Stream', stream === 'img' ? 'IMG' : 'CMG'));

  // One question per screen: the prompt, one focused input, Continue (Enter submits). Text steps share this shell.
  const ask = (q: string, help: string, input: ReactNode, ready: boolean, skippable = false) => (
    <form onSubmit={(e) => { e.preventDefault(); if (ready) advance(); }}>
      <h1 style={S.q}>{q}</h1>
      <p style={S.help}>{help}</p>
      {input}
      <div style={S.bar}>
        <button className="btn btn-primary" type="submit" disabled={!ready} data-testid="guided-continue">Continue</button>
        {skippable && <button className="btn btn-ghost" type="button" onClick={advance} data-testid="guided-skip-step">Skip for now</button>}
      </div>
    </form>
  );

  return (
    <main style={S.main} data-testid="guided-start">
      <button className="btn btn-ghost" style={S.back} onClick={retreat} data-testid="guided-back"><IconBack aria-hidden="true" /> Back</button>
      {step === 'name' && <p style={S.intro}>Let’s set up your CV together — a few quick questions, then we’ll build it section by section. Your words are what go on the page; we just ask.</p>}
      {doneRows.length > 0 && <div style={S.doneList}>{doneRows}</div>}

      {step === 'name' && ask(
        'First — what’s your full name?',
        'As you’d like it to appear on the CV itself — usually your legal name, the way CaRMS knows it.',
        <input style={S.input} autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Amara Okafor" data-testid="guided-name" />,
        !!name.trim(),
      )}

      {step === 'city' && ask(
        'Which city are you based in?',
        'This sits in the CV’s contact header — city and province is the usual form.',
        <input style={S.input} autoFocus value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Halifax, NS" data-testid="guided-city" />,
        !!city.trim(),
      )}

      {step === 'phone' && ask(
        'What’s the best phone number to list?',
        'Optional — programs rarely call, but the header usually carries one. You can add it later.',
        <input style={S.input} autoFocus type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="e.g. 902 555 0134" data-testid="guided-phone" />,
        !!phone.trim(),
        true, // skippable
      )}

      {step === 'specialty' && ask(
        'Which specialty is this CV for?',
        'Everything from here — the sections, the read, the guidance — is shaped by what programs in this area tend to look for. You can change it later.',
        <select style={S.input} autoFocus value={specialtyId} onChange={(e) => setSpecialtyId(e.target.value)} data-testid="guided-specialty">
          <option value="">— choose a specialty —</option>
          {specialties.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>,
        !!specialtyId,
      )}

      {step === 'stream' && ask(
        'Which stream are you applying through?',
        'IMG-stream applicants get a few extra presence checks for things programs commonly expect.',
        <select style={S.input} autoFocus value={stream} onChange={(e) => setStream(e.target.value)} data-testid="guided-stream">
          <option value="">— choose a stream —</option>
          <option value="cmg">CMG — Canadian medical graduate</option>
          <option value="img">IMG — international medical graduate</option>
        </select>,
        !!stream,
      )}

      {step === 'confirm' && (
        <div>
          <h1 style={S.q}>That’s the setup — ready to build?</h1>
          <p style={S.help}>
            We’ll walk your <strong>{specName}</strong> CV section by section. You write every word; we’ll ask the
            questions a reviewer would.
          </p>
          <div style={S.bar}>
            <button className="btn btn-primary" onClick={finish} disabled={saving} data-testid="guided-finish">
              {saving ? 'Saving…' : <>Start building your CV <IconBack aria-hidden="true" style={{ transform: 'rotate(180deg)' }} /></>}
            </button>
          </div>
          {error && <p style={S.err} data-testid="guided-error">{error}</p>}
        </div>
      )}

      {step !== 'confirm' && (
        <p style={S.skipLine}>
          Prefer a form? <button className="btn btn-ghost" style={S.skipBtn} onClick={onSkip} data-testid="guided-skip">Use the standard editor</button>
        </p>
      )}
    </main>
  );
}

const S: Record<string, CSSProperties> = {
  main: { maxWidth: 620, margin: '40px auto', padding: '0 var(--space-4)', fontFamily: 'var(--sans)', lineHeight: 'var(--lh-body)' },
  back: { marginLeft: 0, paddingLeft: 0, fontSize: 'var(--fs-small)', marginBottom: 'var(--space-2)' },
  intro: { color: 'var(--muted)', margin: '0 0 var(--space-5)' },
  q: { fontSize: 24, margin: '0 0 var(--space-1)' },
  help: { color: 'var(--muted)', margin: 'var(--space-1) 0 var(--space-4)' },
  input: { display: 'block', padding: 'var(--space-2)', fontSize: 15, marginTop: 'var(--space-1)', minWidth: 280, maxWidth: '100%' },
  bar: { display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap', marginTop: 'var(--space-4)' },
  doneList: { borderLeft: '2px solid var(--rule)', padding: '2px 0 2px var(--space-3)', margin: '0 0 var(--space-5)' },
  doneRow: { display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)', fontSize: 'var(--fs-body)', margin: 'var(--space-1) 0' },
  doneLabel: { color: 'var(--muted)', minWidth: 76 },
  doneValue: { fontWeight: 600 },
  doneEdit: { fontSize: 'var(--fs-label)', padding: '0 var(--space-1)' },
  skipLine: { marginTop: 28, fontSize: 'var(--fs-small)', color: 'var(--muted)' },
  skipBtn: { fontSize: 'var(--fs-small)', padding: '0 2px' },
  err: { color: 'var(--danger)', marginTop: 'var(--space-3)' },
};
